/**
 * Today's attendance for one child, as the parent screens show it - and whether the child is
 * away, in which case nothing may claim they are in class.
 *
 * A saved leave writes "Leave" rows for its school days straight away (StudentLeaveService),
 * so the monthly attendance call already says "leave" for today before any register is taken.
 * The header used to show "In class • Spanish" from the timetable next to its own "On Leave"
 * chip, and the Home banner "Class in session" likewise, because neither looked at this.
 */
export type TodayAttendanceStatus = 'present' | 'absent' | 'leave' | null;

/** "YYYY-MM-DD" for a local date. */
export const localDateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The days list out of the monthly attendance response, whichever shape the API answered in. */
export function attendanceDays(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  const obj = data as { days?: unknown; data?: unknown } | null;
  if (obj && Array.isArray(obj.days)) return obj.days;
  if (obj && Array.isArray(obj.data)) return obj.data;
  return [];
}

/**
 * Today's status from the monthly attendance response; null when today has no record.
 *
 * A late arrival is in school, so it reads as present (bug 17502: a child on leave who then
 * came in late had their leave overridden, and the header showed no status at all).
 */
export function todayStatusFrom(data: unknown, today: Date = new Date()): TodayAttendanceStatus {
  const key = localDateKey(today);
  const record = attendanceDays(data).find((r: any) => r?.date && String(r.date).startsWith(key));
  const status = record?.status ? String(record.status).trim().toLowerCase() : '';
  if (status === 'late') return 'present';
  return status === 'present' || status === 'absent' || status === 'leave' ? status : null;
}

/** On leave or marked absent today: not in school, so never "in class". */
export const isAwayToday = (status: TodayAttendanceStatus): boolean =>
  status === 'leave' || status === 'absent';
